import type {CancellablePromise} from './types';

interface PollOptions {
  interval?: number;
  onTimeout?: () => void;
  timeout?: number;
}

/**
 * Use this function to poll a condition until it returns true.
 * If the condition returns false, it will keep polling until the timeout is reached. (if provided)
 * If the timeout is reached, it will call the onTimeout function.
 *
 * It returns a function that can be used to stop polling.
 *
 * @example
 *
 * const cleanup = poll(() => Boolean(isReady), () => {
 *   console.log('isReady');
 * }, {timeout: 1000});
 *
 * // stop polling
 * cleanup();
 */
export const poll = (
  condition: () => boolean,
  onSuccess: () => void,
  options: PollOptions = {},
  startTime: number = Date.now(),
): (() => void) => {
  const {interval = 100, onTimeout, timeout} = options;

  if (!condition()) {
    if (timeout && Date.now() - startTime >= timeout) {
      onTimeout?.();

      return () => {};
    }

    const timeoutId = setTimeout(
      () => (cleanup = poll(condition, onSuccess, options, startTime)),
      interval,
    );

    let cleanup = () => clearTimeout(timeoutId);

    return () => cleanup();
  } else {
    onSuccess();

    return () => {};
  }
};

interface PollAsyncOptions {
  interval?: number;
  timeout?: number;
  timeoutMessage?: string;
}

/**
 * Use this function to poll a condition until it returns true.
 *
 * It returns a promise that resolves when the condition returns true.
 * If the condition returns false, it will keep polling until the timeout is reached. (if provided)
 * If the timeout is reached, it will reject with an error.
 *
 * @example
 *
 * const pollingPromise = pollAsync(() => Boolean(isReady), {timeout: 1000});
 *
 * // stop polling
 * pollingPromise.cancel();
 *
 * try {
 *   await promise;
 * } catch (error) {
 *   console.error(error);
 * }
 */
export const pollAsync = (
  condition: () => boolean,
  options: PollAsyncOptions = {},
): CancellablePromise<void> => {
  let cleanup: () => void;

  const promise = new Promise<void>((resolve, reject) => {
    cleanup = poll(condition, resolve, {
      ...options,
      onTimeout: () =>
        reject(new Error(options.timeoutMessage || 'Polling timed out')),
    });
  }) as CancellablePromise<void>;

  promise.cancel = () => cleanup?.();

  return promise;
};
